import React from 'react'
import { NextPage } from 'next'
import { useRouter } from 'next/router'
import { ContentfulProvider } from '@pcln/contentful-components'
import { FeatureProvider } from '@pcln/feature-components'
import Layout from '@/components/Layout'
import { CustomContext, PageProps, QueryString } from '@/types'
import Home from '@/components/Home'
import BANNER_TEXT_VALUES from '@/components/AppBanner/constants'
import { HOME_PRODUCT_TYPE } from '@/constants/products'
import AppBannerAboveHeaderWithExperiment from '@/components/AppBanner/AppBannerAboveHeaderWithExperiment'
import profileData from '../server/utils/profile-data'
import prePopulateHotelValues from '../server/utils/prepop-helper'
import generateBootstrapData from '../utils/generate-bootstrap-data'
import fetchPCLNHeaderFooterContent from '../server/utils/pcln-header-footer'
import fetchSignInStatus from '../server/utils/signin-status'
import getCookies from '../server/utils/get-cookies'
import getPclnHeaders from '../server/utils/get-pcln-headers'
import getRecommendations from '../server/utils/get-recommendations'
import setiService from '../server/middleware/seti'
import mapSetiResponse from '../utils/map-seti-response'
import TmsScript from '@/components/TmsScript/TmsScript'
import getContentfulData from '../server/utils/get-contentful-data'

const Homepage: NextPage<PageProps> = ({
  headerHTML,
  footerHTML,
  bootstrapData,
  content
}) => {
  const { pathname } = useRouter()
  return (
    <>
      <TmsScript pathname={pathname} />
      <FeatureProvider features={bootstrapData.featureToggles}>
        <ContentfulProvider contentful={content}>
          <AppBannerAboveHeaderWithExperiment
            content={BANNER_TEXT_VALUES[HOME_PRODUCT_TYPE]}
          />
          <Layout headerHTML={headerHTML} footerHTML={footerHTML}>
            <Home />
          </Layout>
        </ContentfulProvider>
      </FeatureProvider>
    </>
  )
}

export async function getServerSideProps(ctx: CustomContext) {
  const { req, res } = ctx
  const pricelineSpecificHeaders = getPclnHeaders(req)
  const dmc = getCookies(req).dmc || ''

  const bootstrapData = generateBootstrapData(req, res)
  const { webstats, referral, customer, appVersion, isMobile } = bootstrapData
  const { emailAddress: email = '' } = customer
  const { clientGUID: cguid } = webstats

  const [signInStatus, { content, experimentNames }] = await Promise.all([
    fetchSignInStatus(cguid, dmc, pricelineSpecificHeaders),
    getContentfulData({
      contentTypeId: 'pageHome',
      contentPreview: ctx.query['content-preview'] === 'true'
    })
  ])

  const [
    setiResponseEvents,
    profileDataResponse,
    hotelPrePopData,
    pclnHeaderFooterContent,
    recommendedCityIds
  ] = await Promise.all([
    setiService(req, {
      appVersion,
      allowSetiOverrides: true,
      webstats,
      referral,
      reqQueryParams: ctx.query,
      ...(experimentNames && {
        additionalExperiments: experimentNames
      })
    }),
    profileData({
      dmc,
      cguid,
      req,
      email,
      signedIn: signInStatus.signedIn
    }),
    prePopulateHotelValues(ctx.req.query as QueryString),
    fetchPCLNHeaderFooterContent(req, res, isMobile),
    signInStatus.signedIn
      ? getRecommendations(cguid, dmc, email, pricelineSpecificHeaders)
      : Promise.resolve({ cityId: [] })
  ])
  const { mostRecentBooking, loyaltyTier } = profileDataResponse
  const prePopData = hotelPrePopData ? { hotels: hotelPrePopData } : null

  return {
    props: {
      ...pclnHeaderFooterContent,
      content,
      bootstrapData: {
        ...bootstrapData,
        fullSETI: setiResponseEvents,
        seti: mapSetiResponse(setiResponseEvents),
        signInStatus,
        mostRecentBooking,
        vipTier: loyaltyTier,
        recommendedCityIds,
        ...(prePopData && { prePopData })
      }
    }
  }
}

export default Homepage
